import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Flag, ArrowLeft, MailCheck } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", {
        email: email.trim(),
        redirect_origin: window.location.origin,
      });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-flag-ivory flex flex-col">
      <div className="stripe-accent" />
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <Link to="/login" className="flex items-center gap-2 justify-center font-display font-black text-2xl text-flag-navy mb-6" data-testid="forgot-logo">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-flag-red text-white">
              <Flag className="h-4 w-4" />
            </span>
            Electable
          </Link>
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 sm:p-8">
            {!done ? (
              <>
                <h1 className="font-display text-2xl font-bold text-flag-navy">Reset your password</h1>
                <p className="mt-1 text-slate-600">Enter the email on your account and we&rsquo;ll create a reset link.</p>
                <form onSubmit={onSubmit} className="mt-6 space-y-4">
                  <div>
                    <Label htmlFor="email" className="text-sm font-semibold text-slate-800">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      data-testid="forgot-email-input"
                      className="mt-1 h-12 text-base"
                      placeholder="you@campaign.com"
                    />
                  </div>
                  {error && (
                    <div className="text-sm text-flag-red bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="forgot-error">{error}</div>
                  )}
                  <Button type="submit" disabled={loading} data-testid="forgot-submit-btn" className="w-full h-12 bg-flag-red hover:bg-flag-red-hover text-white font-bold text-base">
                    {loading ? "Sending…" : "Send reset link"}
                  </Button>
                </form>
              </>
            ) : (
              <div data-testid="forgot-done">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-green-800">
                  <MailCheck className="h-6 w-6" />
                </div>
                <h1 className="font-display text-2xl font-bold text-flag-navy mt-4">Check your email</h1>
                <p className="mt-2 text-slate-600">
                  If an account exists for <strong>{email}</strong>, a reset link has been created. It expires in 1 hour and can only be used once.
                </p>
                <p className="mt-3 text-xs text-slate-500">
                  Heads up: email delivery isn&rsquo;t wired in this environment yet. If you&rsquo;re the admin, the link is available in the Admin console.
                </p>
              </div>
            )}
            <Link to="/login" className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-flag-navy hover:text-flag-red" data-testid="forgot-back-link">
              <ArrowLeft className="h-3 w-3" /> Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
